import React, { useRef } from "react";

import { RiArrowDownSLine } from "react-icons/ri";

import { useRecolhimentoAnimado } from "../../../components/TrilhaLudica";

/**
 * A MOLDURA de um tipo de conquista — o painel com cabeçalho, contador e recolher.
 *
 * Existe para que "Minhas Conquistas" tenha UMA gramática, e não uma por tipo. Hoje
 * são medalhas e cartas; amanhã pode ser troféu de turma, selo de projeto, o que
 * vier — e o custo de acrescentar tem de ser uma entrada em `tiposDeConquista.js`,
 * não um layout novo. Enquanto cada tipo desenhava o próprio cabeçalho, a página
 * ganhava um dialeto por seção: dois títulos de tamanhos diferentes, um com contador
 * e outro sem.
 *
 * O CONTADOR VEM DE FORA, pelo `resumo`, e não é o painel que o calcula: quem sabe
 * contar medalha é a seção de medalhas, e quem sabe contar carta é a coleção. O
 * painel só sabe que todo tipo de conquista tem um "quantas de quantas" — que é
 * justamente o que faz uma gramática única ser possível.
 *
 * RECOLHER É UM `<button aria-expanded>` de verdade, não um `<details>`: o
 * `<details>` nativo é mais barato, mas o triângulo e o comportamento dele variam
 * entre navegadores, e o AVA precisa que a mesma tela pareça a mesma coisa no Chrome
 * da escola e no Safari do tablet. Com botão, `aria-expanded` diz o estado ao leitor
 * de tela e o CSS é nosso.
 *
 * O CONTEÚDO É DESMONTADO ao recolher (e não escondido com `display: none`), e a
 * razão é a coleção: são até 110 imagens, e a seção recolhida não deve pagar por
 * elas. Quem carrega dado o carrega de novo ao reabrir — o que, no caso da coleção, é
 * uma requisição a um endpoint idempotente.
 *
 * DESDE 20/08/2026 O DESMONTE ESPERA A ANIMAÇÃO. Recolher e abrir agora percorrem a
 * altura em vez de cortar seco, e o que sumiu da árvore não anima — então
 * `useRecolhimentoAnimado` (do módulo da trilha, que anima a grade de cada região com
 * a mesma mecânica) adia o desmonte até a altura chegar a zero. A decisão de desmontar
 * fica de pé: o que mudou é ela acontecer 240 a 460ms depois.
 *
 * @param {string}   titulo
 * @param {string}   [descricao]  uma linha sobre o que o tipo é
 * @param {Function} Icone        componente de ícone (react-icons)
 * @param {object}   [resumo]     { conquistadas, total }
 * @param {boolean}  aberta
 * @param {Function} onAlternar
 */
function SecaoDeConquista({
  id,
  titulo,
  descricao,
  Icone,
  resumo,
  aberta,
  onAlternar,
  children
}) {
  const idDoConteudo = `conquistas-${id}-conteudo`;
  const temResumo = resumo && resumo.total > 0;

  /* `oculto` é "não monte o conteúdo", e chega DEPOIS de a altura fechar — é o que
     permite animar algo que vai sair da árvore. Ver o hook. */
  const conteudo = useRef(null);
  const { oculto } = useRecolhimentoAnimado(conteudo, aberta);

  return (
    <section
      className={`conquistas__secao${
        aberta ? "" : " conquistas__secao--recolhida"
      }`}
    >
      <h5 className="conquistas__secao-titulo">
        <button
          type="button"
          className="conquistas__secao-botao"
          onClick={onAlternar}
          aria-expanded={aberta}
          aria-controls={idDoConteudo}
        >
          {Icone && (
            <span className="conquistas__secao-icone" aria-hidden="true">
              <Icone size={20} />
            </span>
          )}

          <span className="conquistas__secao-nome">
            {titulo}
            {descricao && (
              <span className="conquistas__secao-descricao">{descricao}</span>
            )}
          </span>

          {/* O CONTADOR SÓ APARECE QUANDO HÁ TOTAL. Enquanto o dado não chegou, o
              resumo é `null` e uma pílula "0 / 0" mentiria — diria "não há nada a
              conquistar", que é o oposto do que a tela quer dizer. */}
          {temResumo && (
            <span className="conquistas__secao-contador">
              <strong>{resumo.conquistadas}</strong>
              <span aria-hidden="true"> / </span>
              <span className="conquistas__secao-contador-total">
                <span className="conquistas__so-leitor">de </span>
                {resumo.total}
              </span>
            </span>
          )}

          {/* O chevron gira; `aria-hidden` porque o estado já está no
              `aria-expanded` do botão, e anunciar a seta seria dizer duas vezes. */}
          <span className="conquistas__secao-chevron" aria-hidden="true">
            <RiArrowDownSLine size={22} />
          </span>
        </button>
      </h5>

      {/* `id` sempre presente, mesmo recolhido: `aria-controls` aponta para ele, e um
          alvo que só existe quando aberto deixa a referência quebrada justamente no
          estado em que o leitor de tela precisa dela. */}
      <div
        ref={conteudo}
        className="conquistas__secao-conteudo"
        id={idDoConteudo}
      >
        {!oculto && children}
      </div>
    </section>
  );
}

export default SecaoDeConquista;
